import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, Tabs } from 'antd';
import { FJ120, FJ121, j202Api } from '../../services/j202Api';
import { BatchAcceptanceBridge, pwj202f1AcceptanceManifest } from '../../features/acceptance';
import { useModulePermission } from '../../hooks/usePermission';
import { useJ202Mode } from './hooks/useJ202Mode';
import { useJ202Record } from './hooks/useJ202Record';
import { useJ202Navigation } from './hooks/useJ202Navigation';
import { useJ202Approval } from './hooks/useJ202Approval';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Details from './components/Tab2Details';
import { SharedActionBar, ApprovalState } from '../../components/SharedActionBar';
import ApprovalHistoryModal from './components/ApprovalHistoryModal';
import { statusLabels, statusBadgeClass } from './helpers';
import './J202Form.css';

const MODULE_CODE = 'pwj202f1';

const J202Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const [activeTab, setActiveTab] = useState('1');
  const permissions = useModulePermission(MODULE_CODE);

  const { mode, setMode, isEditing } = useJ202Mode();
  const {
    record, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    addDetailRow, deleteDetailRow,
  } = useJ202Record();

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useJ202Navigation(
    useCallback(async (id: string) => { await loadRecord(id); }, [loadRecord]),
  );

  const {
    approvalHistoryVisible, setApprovalHistoryVisible,
    handleSubmit, handleApprove, handleReject, handleWriteoff, handleViewHistory,
  } = useJ202Approval({ record, isEditing, loadRecord });

  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (reviewId) {
          const idx = ids.indexOf(reviewId);
          if (idx >= 0) setCurrentIdx(idx);
          await loadRecord(reviewId);
        } else if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecord(ids[0]);
        }
      } catch {
        console.error('Failed to load J202 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddAction = useCallback(async () => {
    if (isEditing && mode === 'add') {
      await handleSave(mode, async (result: FJ120) => {
        const ids = await loadList();
        const newIdx = ids.indexOf(result.fj120_01);
        setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      setActiveTab('1');
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd]);

  const handleModifyAction = useCallback(async () => {
    if (isEditing && mode === 'edit') {
      await handleSave(mode, async (result: FJ120) => {
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('edit');
    }
  }, [isEditing, mode, handleSave, applyResult, setMode]);

  const handleDeleteAction = useCallback(() => {
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除繳庫單 ${record?.fj120_01} 嗎？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        await handleDelete(async () => {
          const ids = await loadList();
          if (ids.length > 0) {
            setCurrentIdx(0);
            await loadRecord(ids[0]);
          } else {
            resetForAdd();
          }
          setMode('browse');
        });
      },
    });
  }, [record, handleDelete, loadList, setCurrentIdx, loadRecord, resetForAdd, setMode]);

  const handleExit = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      setMode('browse');
    } else {
      navigate('/erp');
    }
  }, [isEditing, revertToRecord, setMode, navigate]);

  const handleDetailChange = useCallback((idx: number, key: keyof FJ121, value: FJ121[keyof FJ121]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], [key]: value };
      }
      return next;
    });
  }, [setDetails]);

  const status = record?.fj120_80 || '';

  return (
    <div className="j202-form">
      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <div className="breadcrumb-inline">
              <a href="/erp">ERP</a>
              <span>/</span>
              <span className="current">樣品繳庫單</span>
            </div>
            <span className="title-separator">|</span>
            <h1>樣品繳庫單 (pwj202f1)</h1>
            {record?.fj120_01 && (
              <span className={`badge ${statusBadgeClass(status)}`}>
                {statusLabels[status] || status}
              </span>
            )}
          </div>
          <div className="page-title-right">
            <span style={{ fontSize: 12, color: '#999' }}>
              {record?.fj120_01 || '(新增)'}
            </span>
          </div>
        </div>

        <div className="form-info-bar">
          <div className="field-group">
            <label data-contract-field="fj120_01">樣品繳庫單號</label>
            <strong>{formData.fj120_01 || '--'}</strong>
          </div>
          <div className="field-group">
            <label data-contract-field="form_mode">模式</label>
            <span className={`badge ${isEditing ? 'badge-warning' : 'badge-default'}`}>
              {mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽'}
            </span>
          </div>
        </div>

        <div className="form-content">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: '1',
                label: '基本資料',
                children: (
                  <Tab1BasicInfo
                    formData={formData}
                    isEditing={isEditing}
                    getField={getField}
                    setField={setField}
                  />
                ),
              },
              {
                key: '2',
                label: `明細 (${details.length})`,
                children: (
                  <Tab2Details
                    details={details}
                    isEditing={isEditing}
                    onAdd={addDetailRow}
                    onDelete={deleteDetailRow}
                    onDetailChange={handleDetailChange}
                  />
                ),
              },
            ]}
          />
        </div>

        <SharedActionBar
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!record}
          currentIdx={currentIdx}
          recordCount={allIds.length}
          onNavFirst={() => navTo(0, isEditing)}
          onNavPrev={() => navTo(currentIdx - 1, isEditing)}
          onNavNext={() => navTo(currentIdx + 1, isEditing)}
          onNavLast={() => navTo(allIds.length - 1, isEditing)}
          onAdd={handleAddAction}
          onModify={handleModifyAction}
          onDelete={handleDeleteAction}
          approval={{
            statusCode: record?.fj120_80 || '',
            isApproved: record?.fj120_80 === 'P',
            isPending: ['D', 'S'].includes(record?.fj120_80 || ''), // 統一碼 D + 舊碼 S 過渡容忍
            isLocked: ['P', 'D', 'S'].includes(record?.fj120_80 || ''),
          }}
          onSubmit={handleSubmit}
          onApprove={handleApprove}
          onReject={handleReject}
          onWriteoff={handleWriteoff}
          onHistory={handleViewHistory}
          onExit={handleExit}
          canCreate={permissions.canCreate}
          canEdit={permissions.canEdit}
          canDelete={permissions.canDelete}
          canApprove={permissions.canApprove}
        />
        <BatchAcceptanceBridge
          manifest={pwj202f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record?.fj120_01 ? [record.fj120_01] : []}
          keyOf={(item) => item.fj120_01}
          queryExact={(key) => j202Api.get(key)}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={() => allIds[0] && navTo(0, false)}
          autoFillDisabled={allIds.length === 0}
          screenDraft={{ master: formData, details }}
        />

        <ApprovalHistoryModal
          visible={approvalHistoryVisible}
          requestNo={record?.fj120_01 || ''}
          onClose={() => setApprovalHistoryVisible(false)}
        />
      </div>
    </div>
  );
};

export default J202Form;
